import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement
} from "@stripe/react-stripe-js";
import { memo } from "react";
import { Field } from "redux-form/immutable";

import { Column, Columns, Stack, Text, useTheme } from "@seneca/senekit";

import "common/assets/fonts/paymentfont/scss/paymentfont.scss";

import { FormTextInput } from "features/forms/components/FormTextInput";

import { useCheckStripeHasLoaded } from "../../hooks/useCheckStripeHasLoaded";
import styles from "./CreditCardForm.module.scss";
import { StripeElementPlaceholder } from "./StripeElementPlaceholder";

type Props = {
  postCodeField?: string;
};

function CreditCardForm({ postCodeField }: Props) {
  const [loadingStripeJS, setLoaded] = useCheckStripeHasLoaded();

  const theme = useTheme();

  const stripeElementStyles = {
    base: {
      fontFamily: "Mulish, sans-serif",
      fontSize: "16px",
      color: theme["neutral-high-content"],
      fontSmoothing: "antialiased",
      "::placeholder": {
        color: theme["neutral-low-content"]
      }
    },
    invalid: {
      color: theme["error-high-surface"]
    }
  };

  return (
    <Stack space="xlarge">
      <label className={styles.cardNumberBlock}>
        <Text size="xsmall" weight="bold" tone="neutral">
          Card number
        </Text>
        <StripeElementPlaceholder loading={loadingStripeJS as boolean}>
          <CardNumberElement
            className={styles.cardDetailsField}
            options={{ placeholder: "", style: stripeElementStyles }}
            onReady={setLoaded as any}
            id="CardNumber"
          />
        </StripeElementPlaceholder>
      </label>
      <Columns space="xlarge" collapseBelow="tablet">
        <Column>
          <label className={styles.label}>
            <Text size="xsmall" weight="bold" tone="neutral">
              Expiry date
            </Text>
            <StripeElementPlaceholder loading={loadingStripeJS as boolean}>
              <CardExpiryElement
                className={styles.cardDetailsField}
                options={{ style: stripeElementStyles }}
                id="Expiry"
              />
            </StripeElementPlaceholder>
          </label>
        </Column>
        <Column>
          <label className={styles.label}>
            <Text size="xsmall" weight="bold" tone="neutral">
              CVV
            </Text>
            <StripeElementPlaceholder loading={loadingStripeJS as boolean}>
              <CardCvcElement
                className={styles.cardDetailsField}
                options={{ placeholder: "", style: stripeElementStyles }}
                id="CVV"
              />
            </StripeElementPlaceholder>
          </label>
        </Column>
        <Column>
          <label className={styles.label}>
            <Text size="xsmall" weight="bold" tone="neutral">
              Postcode
            </Text>
            <Field
              name={postCodeField}
              component={FormTextInput}
              type="text"
              autoCapitalize="none"
            />
          </label>
        </Column>
      </Columns>
    </Stack>
  );
}

export default memo<Props>(CreditCardForm);
